import { FC } from 'react';
import { TbBolt, TbHeadset, TbLine, TbRuler, TbShieldBolt, TbSparkles } from 'react-icons/tb';

import { Button, Card, Group, Progress, SimpleGrid, Stack, Text, ThemeIcon } from '@mantine/core';

import { CreditUsageType } from 'models';
import { IntlHelpers } from 'helpers/IntlHelpers';

import { PERSONA_PRESETS, PersonaPreset } from 'components/subscriptions/pricing-page/data/pricing-page-data';
import { useCreditBalanceDetails } from 'hooks/use-credit-balance-details';
import { useCurrentTeam } from 'hooks/use-current-team';

import classes from './UsageGuidancePanel.module.scss';

const USAGE_THRESHOLD = 200;

const USAGE_TYPE_ICONS: Partial<Record<CreditUsageType, typeof TbBolt>> = {
    [CreditUsageType.POWER_FLOW_SIMULATION]: TbBolt,
    [CreditUsageType.WIRE_SIZING_SIMULATION]: TbLine,
    [CreditUsageType.FAULT_SIMULATION]: TbShieldBolt,
    [CreditUsageType.SUPPORT]: TbHeadset,
    [CreditUsageType.DIAGRAM_AI_MESSAGE]: TbSparkles,
    [CreditUsageType.WIRE_SIZING]: TbRuler,
};

const USAGE_TYPE_LABELS: Partial<Record<CreditUsageType, string>> = {
    [CreditUsageType.POWER_FLOW_SIMULATION]: 'Power flow',
    [CreditUsageType.WIRE_SIZING_SIMULATION]: 'Wire sizing',
    [CreditUsageType.FAULT_SIMULATION]: 'Fault sims',
    [CreditUsageType.SUPPORT]: 'Expert support',
    [CreditUsageType.DIAGRAM_AI_MESSAGE]: 'AI design agent',
    [CreditUsageType.WIRE_SIZING]: 'Wire sizing checks',
};

type UsageGuidancePanelProps = {
    onSelectPreset?: (creditAmount: number) => void;
};

export const UsageGuidancePanel: FC<UsageGuidancePanelProps> = ({ onSelectPreset }) => {
    const team = useCurrentTeam();
    const { creditBalanceInfo, isLoading } = useCreditBalanceDetails(team);

    if (isLoading) return null;

    const monthlyUsage = creditBalanceInfo?.monthlyUsage ?? 0;
    const usage = creditBalanceInfo?.usage;

    if (monthlyUsage >= USAGE_THRESHOLD && usage) {
        return <ActualUsageView monthlyUsage={monthlyUsage} usage={usage} onSelectPreset={onSelectPreset} />;
    }

    return <PersonaGuidanceView onSelectPreset={onSelectPreset} />;
};

type ActualUsageViewProps = {
    monthlyUsage: number;
    usage: Partial<Record<CreditUsageType, number>>;
    onSelectPreset?: (creditAmount: number) => void;
};

const ActualUsageView: FC<ActualUsageViewProps> = ({ monthlyUsage, usage, onSelectPreset }) => {
    const usageEntries = Object.entries(usage)
        .filter(([, count]) => count && count > 0)
        .sort(([, a], [, b]) => (b ?? 0) - (a ?? 0)) as [CreditUsageType, number][];

    const suggestedAmount = Math.ceil(monthlyUsage / 100) * 100;

    return (
        <Card withBorder radius="md" p="md" className={classes.panel}>
            <Stack gap="sm">
                <Text fw={600} fz="sm">
                    Based on your usage
                </Text>
                <Text fz="sm" c="dimmed">
                    You typically use about{' '}
                    <Text span fw={700}>
                        {IntlHelpers.formatNumber(monthlyUsage)} credits / month
                    </Text>
                </Text>

                <Stack gap={6}>
                    {usageEntries.map(([type, count]) => {
                        const Icon = USAGE_TYPE_ICONS[type];
                        const label = USAGE_TYPE_LABELS[type];
                        if (!Icon || !label) return null;

                        const ratio = monthlyUsage > 0 ? (count / monthlyUsage) * 100 : 0;

                        return (
                            <Group key={type} gap="sm" wrap="nowrap">
                                <Icon size={14} style={{ flexShrink: 0 }} />
                                <Text fz="xs" w={90} style={{ flexShrink: 0 }}>
                                    {label}
                                </Text>
                                <Progress value={ratio} color="cyan" size="sm" style={{ flex: 1 }} />
                                <Text fz="xs" fw={500} w={60} ta="right" style={{ flexShrink: 0 }}>
                                    {IntlHelpers.formatNumber(count)}
                                </Text>
                            </Group>
                        );
                    })}
                </Stack>

                {onSelectPreset && (
                    <Button variant="light" size="compact-sm" onClick={() => onSelectPreset(suggestedAmount)} mt={4}>
                        Use suggested: {IntlHelpers.formatNumber(suggestedAmount)} credits / month
                    </Button>
                )}
            </Stack>
        </Card>
    );
};

type PersonaGuidanceViewProps = {
    onSelectPreset?: (creditAmount: number) => void;
};

const PersonaGuidanceView: FC<PersonaGuidanceViewProps> = ({ onSelectPreset }) => {
    return (
        <Card withBorder radius="md" p="md" className={classes.panel}>
            <Stack gap="sm">
                <Text fw={600} fz="sm">
                    Not sure how many credits you need?
                </Text>
                <Text fz="sm" c="dimmed">
                    Pick the profile that best matches your work:
                </Text>

                <SimpleGrid cols={1} spacing="xs">
                    {PERSONA_PRESETS.map((preset) => (
                        <PersonaMiniCard key={preset.id} preset={preset} onSelect={onSelectPreset} />
                    ))}
                </SimpleGrid>
            </Stack>
        </Card>
    );
};

type PersonaMiniCardProps = {
    preset: PersonaPreset;
    onSelect?: (creditAmount: number) => void;
};

const PersonaMiniCard: FC<PersonaMiniCardProps> = ({ preset, onSelect }) => {
    const Icon = preset.icon;

    return (
        <Card
            withBorder
            radius="sm"
            p="sm"
            className={classes.personaCard}
            onClick={() => onSelect?.(preset.totalMonthlyCredits)}
            style={{ cursor: onSelect ? 'pointer' : undefined }}
        >
            <Group gap="sm" wrap="nowrap">
                <ThemeIcon variant="light" size="md" color="gray">
                    <Icon size={14} />
                </ThemeIcon>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <Text fz="sm" fw={600}>
                        {preset.title}
                    </Text>
                    <Text fz="xs" c="dimmed" truncate>
                        {preset.subtitle}
                    </Text>
                </div>
                <Text fz="sm" fw={700} style={{ flexShrink: 0 }}>
                    {IntlHelpers.formatNumber(preset.totalMonthlyCredits)} credits/mo
                </Text>
            </Group>
        </Card>
    );
};
